import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  FileText,
  Lock,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ProductCombobox, {
  type ProductOption,
} from "@/components/ProductCombobox";
import EntityCombobox from "@/components/EntityCombobox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import WaybillPrint, {
  type WaybillPrintData,
} from "@/components/WaybillPrint";
import type { PdfWaybill } from "@/lib/waybillPdf";
import type { PdfCompany } from "@/lib/pdf";
import { formatNumber } from "@/lib/format";
import { renderReactToPdf, safePdfFilename } from "@/lib/htmlPdf";

interface QuoteOption {
  id: number;
  quote_number: string;
  customer_id?: number | null;
  customer_name: string;
  telephone?: string;
  location?: string;
  total_amount?: number;
  total?: number;
  status?: string;
  notes?: string;
  locked_by_waybill?: boolean;
  has_waybill?: boolean;
}

interface QuoteApi {
  id: number;
  quote_number: string;
  customer_name: string;
  telephone: string;
  location: string;
  total_amount: number;
  notes: string;
  // Quotation-level attributes carried through to the waybill PDF header
  // strip. Optional because pre-migration quotes return null/blank.
  profile?: string | null;
  color?: string | null;
  items: Array<{
    product_id?: number | null;
    product_name: string;
    description: string;
    unit: string;
    quantity: number;
    length_breakdown: Array<{ length: number; quantity: number }>;
  }>;
}

interface LengthQtyEntry {
  length: number;
  quantity: number;
}

interface WaybillItem {
  // Catalog product FK if the user picked from the dropdown, null for
  // free-text rows. Multiple rows MAY share the same productId with
  // different lengths — no deduplication.
  productId: number | null;
  description: string;
  unit: string;
  // Per-row LENGTH (1 = "no length" for Pcs/Screws). Stored verbatim;
  // calculations treat values <=0 as 1 so TOTAL math still produces a value.
  length: number;
  // User-entered quantity.
  quantity: number;
}

interface WaybillApi {
  id: number;
  waybill_number: string;
  has_sales_invoice?: boolean;
  // Linked sales invoice (via shared quote_id) surfaced by the API. Null
  // when no active invoice exists for the waybill's quotation.
  invoice_number?: string | null;
  invoice_date?: string | null;
  created_by_name?: string | null;
  quote_id: number | null;
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  quotation_ref: string;
  waybill_date: string | null;
  driver_name: string;
  vehicle_number: string;
  status: string;
  notes: string;
  items: Array<{
    line_no: number;
    product_id?: number | null;
    description: string;
    unit: string;
    length?: number;
    quantity: number;
    // Legacy breakdown field — read for backward compat on older rows.
    length_breakdown?: LengthQtyEntry[];
  }>;
}

const BLANK_ITEM: WaybillItem = {
  productId: null,
  description: "",
  unit: "Pieces",
  length: 1,
  quantity: 0,
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function effectiveLength(length: number) {
  // Mirrors the server / PDF helper: blank or non-positive lengths fall
  // back to 1 so the TOTAL QUANTITY column always renders sensibly.
  return Number(length) > 0 ? Number(length) : 1;
}

function rowTotal(it: WaybillItem) {
  return Math.round(effectiveLength(it.length) * Number(it.quantity || 0) * 100) /
    100;
}

// Convert legacy `length_breakdown` payloads from older waybill rows into a
// single { length, quantity } pair so they slot into the new flat schema
// without losing data when possible.
function itemFromLegacy(it: {
  product_id?: number | null;
  description?: string;
  unit?: string;
  length?: number;
  quantity?: number;
  length_breakdown?: LengthQtyEntry[];
}): WaybillItem {
  const lengthFromColumn = Number(it.length ?? 0);
  const breakdown = Array.isArray(it.length_breakdown)
    ? it.length_breakdown
    : [];
  const firstLength = Number(breakdown[0]?.length ?? 0);
  const firstQty = Number(breakdown[0]?.quantity ?? 0);
  // Prefer the new column when present; otherwise use the first breakdown
  // entry's length; otherwise default to 1.
  const length =
    lengthFromColumn > 0
      ? lengthFromColumn
      : firstLength > 0
        ? firstLength
        : 1;
  // Quantity: if the new column gives us a value, trust it. Old rows stored
  // the computed total in `quantity`, so dividing by the (default) length of
  // 1 still produces the legacy display.
  const quantity =
    it.quantity !== undefined && it.quantity !== null
      ? Number(it.quantity)
      : firstQty;
  const productId =
    it.product_id !== undefined && it.product_id !== null
      ? Number(it.product_id) || null
      : null;
  return {
    productId,
    description: String(it.description ?? ""),
    unit: String(it.unit ?? "Pieces"),
    length,
    quantity,
  };
}

function isQuotationFullyPaid(
  q: { id: number; total_amount?: number; total?: number; status?: string },
  paidMap: Record<number, number>,
) {
  if ((q.status || "").toLowerCase() === "paid") return true;
  const total = Number(q.total_amount ?? q.total ?? 0);
  const paid = Number(paidMap[q.id] ?? 0);
  return total > 0 && paid >= total - 0.01;
}

export default function WaybillForm() {
  const { id } = useParams<{ id: string }>();
  const { pathname, search } = useLocation();
  const nav = useNavigate();
  const { user, can } = useAuth();
  const canEdit = can("waybills.write");
  const isCancelledPreview = pathname.includes("/waybills/cancelled/");
  const isExisting = Boolean(id) && !isCancelledPreview;
  const isPreview =
    isCancelledPreview ||
    (Boolean(id) && !pathname.endsWith("/edit"));
  const editing = isExisting && pathname.endsWith("/edit");
  const wantsAutoOpenPdf = useMemo(
    () => new URLSearchParams(search).get("openPdf") === "1",
    [search],
  );
  const [hasSalesInvoice, setHasSalesInvoice] = useState(false);
  // Linked sales invoice number/date (from the API, via the shared quote).
  // Drive both the PDF header strip and the preview/download gating.
  const [invoiceNumber, setInvoiceNumber] = useState<string | null>(null);
  const [invoiceDate, setInvoiceDate] = useState<string | null>(null);
  const lockedByInvoice = hasSalesInvoice;
  const readOnly = isPreview || !canEdit || lockedByInvoice;
  // User rule: a waybill PDF may NOT be previewed/downloaded until a sales
  // invoice has been generated for it (linked via the shared quote). Cancelled
  // waybills are an audit artifact (and can never have an invoice) so they
  // stay previewable. Everything else requires an invoice.
  const canPreviewPdf = isCancelledPreview || hasSalesInvoice;
  const pdfGateReason =
    "Generate a Sales Invoice for this waybill before previewing/printing.";
  const [activeNumberField, setActiveNumberField] = useState<string | null>(
    null,
  );

  const displayNumber = (fieldId: string, value: number) =>
    activeNumberField === fieldId && Number(value) === 0 ? "" : String(value);

  const [quotes, setQuotes] = useState<QuoteOption[]>([]);
  // Amount paid per quote and available credit per customer, surfaced by the
  // waybills form-support endpoint. Used to offer quotes that are coverable
  // by the customer's "money on account" credit, not just fully-paid ones.
  const [paidByQuote, setPaidByQuote] = useState<Record<number, number>>({});
  const [creditByCustomer, setCreditByCustomer] = useState<
    Record<number, number>
  >({});
  const [productCatalog, setProductCatalog] = useState<ProductOption[]>([]);
  const [editingQuoteOption, setEditingQuoteOption] =
    useState<QuoteOption | null>(null);

  const [waybillNumber, setWaybillNumber] = useState("");
  const [quoteId, setQuoteId] = useState<number | null>(null);
  const [quotationRef, setQuotationRef] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [waybillDate, setWaybillDate] = useState(todayIso);
  const [driverName, setDriverName] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [status, setStatus] = useState("draft");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<WaybillItem[]>([{ ...BLANK_ITEM }]);
  const [createdByName, setCreatedByName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Strict mode: every waybill row must resolve to a catalog product. The
  // red ring + helper text only appear after a failed Save attempt so the
  // user isn't yelled at while still typing.
  const [showProductErrors, setShowProductErrors] = useState(false);
  const [company, setCompany] = useState<PdfCompany>({});
  // Profile / Color flow in from the source quotation at import time. They
  // are NOT persisted on the waybill, so an existing waybill reopened later
  // will show them blank until the data model carries them.
  const [quoteProfile, setQuoteProfile] = useState("");
  const [quoteColor, setQuoteColor] = useState("");
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [pdfWaybillNumber, setPdfWaybillNumber] = useState("");
  const [postSaveNavId, setPostSaveNavId] = useState<number | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [autoOpenConsumed, setAutoOpenConsumed] = useState(false);

  const totalQuantitySum = useMemo(
    () => items.reduce((sum, it) => sum + rowTotal(it), 0),
    [items],
  );
  // Once items originate from a quotation the grid must mirror the source so
  // the downstream waybill stays traceable. Stacks on top of the existing
  // readOnly / editing locks; lockedByInvoice is a higher lock already covered
  // by readOnly.
  const itemsImportedFromQuote = quoteId != null;
  const itemsLocked = readOnly || itemsImportedFromQuote;
  // Derived header fields (phone, address, notes, date) are never editable
  // on a writable form — only Customer, Quotation #, Driver, and Vehicle
  // may be changed. Mirrors SalesInvoiceForm's itemsLocked pattern.
  const derivedFieldsLocked = !readOnly;
  const showImportLockBanner =
    itemsImportedFromQuote && !lockedByInvoice && !isCancelledPreview;
  const [cancelMeta, setCancelMeta] = useState<{
    reason: string;
    cancelledBy: string;
    cancelledAt: string;
  } | null>(null);

  // Best-effort load of company settings — used to brand the waybill PDF.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ settings: Record<string, string> }>(
          "/settings",
        );
        const s = res.settings ?? {};
        setCompany({
          name: s["company.name"] || undefined,
          email: s["company.email"] || undefined,
          phone: s["company.phone"] || undefined,
          address: s["company.address"] || undefined,
          currency_code: s["currency.code"] || undefined,
          currency_symbol: s["currency.symbol"] || undefined,
        });
      } catch {
        // Non-fatal: PDF will still render with default branding.
      }
    })();
  }, []);

  useEffect(() => {
    // Pickers come from the Waybills-scoped form-support endpoint so
    // granting only the Waybills module is enough — admin does NOT need to
    // tick Customers / Quotations / Receipts / Products. The response
    // contains all the rows; client-side filters the list to the fully-paid
    // non-draft non-waybilled quotations the form actually accepts.
    (async () => {
      try {
        const params = new URLSearchParams();
        if (id && !isCancelledPreview) {
          params.set("exceptWaybillId", id);
        }
        const qs = params.toString();
        const data = await api.get<{
          customers: Array<{
            id: number;
            name: string;
            phone: string;
            address: string;
          }>;
          quotations: QuoteOption[];
          paid_by_quote: Record<string, number>;
          credit_by_customer: Record<string, number>;
          products: ProductOption[];
        }>(`/waybills/form-support${qs ? `?${qs}` : ""}`);
        const allQuotes = data.quotations ?? [];
        const paidMapRaw = data.paid_by_quote ?? {};
        const paidMap: Record<number, number> = {};
        for (const [k, v] of Object.entries(paidMapRaw)) {
          const n = Number(k);
          if (Number.isFinite(n)) paidMap[n] = Number(v ?? 0);
        }
        setPaidByQuote(paidMap);
        const creditMapRaw = data.credit_by_customer ?? {};
        const creditMap: Record<number, number> = {};
        for (const [k, v] of Object.entries(creditMapRaw)) {
          const n = Number(k);
          if (Number.isFinite(n)) creditMap[n] = Number(v ?? 0);
        }
        setCreditByCustomer(creditMap);
        // Filter to the rows the waybill form accepts: not already waybilled
        // (except the editing waybill's own quote), not draft, not locked, and
        // either fully paid OR coverable by the customer's available credit
        // (money on account) — the server auto-creates the applied-from-credit
        // receipt when such a waybill is raised.
        const usable = allQuotes.filter((q) => {
          if (q.has_waybill) return false;
          const status = String(q.status ?? "").trim().toLowerCase();
          if (!status || status === "draft") return false;
          if (q.locked_by_waybill) return false;
          if (isQuotationFullyPaid(q, paidMap)) return true;
          const total = Number(q.total_amount ?? q.total ?? 0);
          const paid = Number(paidMap[q.id] ?? 0);
          const outstanding = total - paid;
          if (outstanding <= 0.01) return true;
          const credit =
            q.customer_id != null ? Number(creditMap[q.customer_id] ?? 0) : 0;
          return credit + 0.01 >= outstanding;
        });
        setQuotes(usable);
        setProductCatalog(data.products ?? []);
      } catch (err) {
        toast.error(
          (err as Error)?.message ||
            "Failed to load customer / quotation / product lists",
        );
      }
    })();
  }, [id, isCancelledPreview]);

  useEffect(() => {
    if (!id) return;

    if (isCancelledPreview) {
      (async () => {
        try {
          const data = await api.get<{
            cancellation: WaybillApi & {
              cancellation_reason: string;
              cancelled_by: string;
              cancelled_at: string | null;
            };
          }>(`/waybills/cancellations/${id}`);
          const c = data.cancellation;
          setWaybillNumber(c.waybill_number);
          setQuotationRef(c.quotation_ref);
          setCustomerName(c.customer_name);
          setCustomerPhone(c.customer_phone);
          setDeliveryAddress(c.delivery_address);
          setWaybillDate(
            c.waybill_date
              ? new Date(c.waybill_date).toISOString().slice(0, 10)
              : todayIso(),
          );
          setDriverName(c.driver_name);
          setVehicleNumber(c.vehicle_number);
          setStatus(c.status || "draft");
          setNotes(c.notes || "");
          setItems(
            (c.items || []).length
              ? c.items.map(itemFromLegacy)
              : [{ ...BLANK_ITEM }],
          );
          setCancelMeta({
            reason: c.cancellation_reason,
            cancelledBy: c.cancelled_by,
            cancelledAt: c.cancelled_at
              ? new Date(c.cancelled_at).toLocaleString()
              : "",
          });
        } catch (err) {
          toast.error((err as Error).message || "Failed to load cancelled waybill");
        }
      })();
      return;
    }

    (async () => {
      try {
        const data = await api.get<{ waybill: WaybillApi }>(`/waybills/${id}`);
        const w = data.waybill;
        setHasSalesInvoice(!!w.has_sales_invoice);
        setInvoiceNumber(w.invoice_number ?? null);
        setInvoiceDate(w.invoice_date ?? null);
        setWaybillNumber(w.waybill_number);
        setQuoteId(w.quote_id);
        setQuotationRef(w.quotation_ref);
        setCustomerName(w.customer_name);
        if (w.quote_id) {
          setEditingQuoteOption({
            id: w.quote_id,
            quote_number: w.quotation_ref || "",
            customer_name: w.customer_name,
          });
        }
        setCustomerPhone(w.customer_phone);
        setDeliveryAddress(w.delivery_address);
        setWaybillDate(
          w.waybill_date
            ? new Date(w.waybill_date).toISOString().slice(0, 10)
            : todayIso(),
        );
        setDriverName(w.driver_name);
        setVehicleNumber(w.vehicle_number);
        setStatus(w.status || "draft");
        setNotes(w.notes || "");
        setItems(
          (w.items || []).length
            ? w.items.map(itemFromLegacy)
            : [{ ...BLANK_ITEM }],
        );
        setCreatedByName(w.created_by_name ?? null);
      } catch (err) {
        toast.error((err as Error).message || "Failed to load waybill");
      }
    })();
  }, [id, isCancelledPreview]);

  const effectiveQuotes = useMemo(() => {
    if (!editingQuoteOption) return quotes;
    if (quotes.some((q) => q.id === editingQuoteOption.id)) return quotes;
    return [editingQuoteOption, ...quotes];
  }, [quotes, editingQuoteOption]);

  // Belt-and-suspenders: even if the server ever regresses on the
  // `excludeDraft=1` filter, the picker must never expose a draft quotation
  // to the user. Treat missing/blank status as draft. The currently-linked
  // quote on an existing waybill is preserved so edit mode can still render
  // its placeholder.
  const nonDraftQuotes = useMemo(() => {
    const editingId = editingQuoteOption?.id ?? null;
    return effectiveQuotes.filter((q) => {
      const status = String(q.status ?? "").trim().toLowerCase();
      const isDraft = status === "" || status === "draft";
      return !isDraft || q.id === editingId;
    });
  }, [effectiveQuotes, editingQuoteOption]);

  const customerOptions = useMemo(
    () =>
      Array.from(
        new Set(nonDraftQuotes.map((q) => q.customer_name).filter(Boolean)),
      ).sort((a, b) => a.localeCompare(b)),
    [nonDraftQuotes],
  );
  const customerQuotes = useMemo(
    () => nonDraftQuotes.filter((q) => q.customer_name === customerName),
    [nonDraftQuotes, customerName],
  );

  // Resolve the selected customer's id (the picker keys on name) so we can
  // look up their available credit, then surface it on the form.
  const selectedCustomerId = useMemo(() => {
    const match = effectiveQuotes.find(
      (q) => q.customer_name === customerName && q.customer_id != null,
    );
    return match?.customer_id ?? null;
  }, [effectiveQuotes, customerName]);
  const selectedCustomerCredit = useMemo(
    () =>
      selectedCustomerId != null
        ? Number(creditByCustomer[selectedCustomerId] ?? 0)
        : 0,
    [selectedCustomerId, creditByCustomer],
  );

  // Outstanding balance on the chosen quote (total minus receipts already
  // recorded against it). When > 0 but covered by credit, the server will
  // settle it from the customer's available credit on save.
  const selectedQuoteOutstanding = useMemo(() => {
    if (quoteId == null) return 0;
    const q = effectiveQuotes.find((x) => x.id === quoteId);
    if (!q) return 0;
    const total = Number(q.total_amount ?? q.total ?? 0);
    const paid = Number(paidByQuote[quoteId] ?? 0);
    return Math.max(0, total - paid);
  }, [quoteId, effectiveQuotes, paidByQuote]);
  const willSettleFromCredit = selectedQuoteOutstanding > 0.01;

  async function importQuotation(quoteIdStr: string) {
    const newId = Number(quoteIdStr);
    if (!newId) return;
    setQuoteId(newId);
    try {
      // Use the Waybill-scoped import endpoint so a user with only Waybills
      // granted (no quotes.read) can still pull items off the chosen quote.
      const data = await api.get<{ quotation: QuoteApi }>(
        `/waybills/quote-import/${newId}`,
      );
      const q = data.quotation;
      setQuotationRef(q.quote_number);
      setCustomerName(q.customer_name);
      setCustomerPhone(q.telephone || "");
      setDeliveryAddress(q.location || "");
      setQuoteProfile((q.profile || "").trim());
      setQuoteColor((q.color || "").trim());
      if (!editing) setWaybillDate(todayIso());
      const loaded = Array.isArray(q.items) ? q.items : [];
      if (loaded.length) {
        // Fan each quotation item out into one waybill row per
        // {length, quantity} pair in its breakdown — the user expects to see
        // every cut listed individually instead of a single collapsed row
        // showing the summed quantity. Falls back to a single row for items
        // without a usable breakdown so we still import legacy data cleanly.
        const expanded: WaybillItem[] = [];
        for (const it of loaded) {
          const description = it.product_name || it.description || "";
          const unit = it.unit || "Pieces";
          const productId =
            it.product_id !== undefined && it.product_id !== null
              ? Number(it.product_id) || null
              : null;
          const breakdown = Array.isArray(it.length_breakdown)
            ? it.length_breakdown.filter(
                (r) =>
                  Number(r?.length || 0) > 0 || Number(r?.quantity || 0) > 0,
              )
            : [];
          if (breakdown.length) {
            for (const row of breakdown) {
              expanded.push({
                productId,
                description,
                unit,
                length: Number(row.length || 0) > 0 ? Number(row.length) : 1,
                quantity: Number(row.quantity || 0),
              });
            }
          } else {
            expanded.push(
              itemFromLegacy({
                product_id: productId,
                description,
                unit,
                quantity: Number(it.quantity || 0),
                length_breakdown: it.length_breakdown,
              }),
            );
          }
        }
        setItems(expanded.length ? expanded : [{ ...BLANK_ITEM }]);
      }
      toast.success("Imported from quotation");
    } catch (err) {
      toast.error((err as Error).message || "Import failed");
    }
  }

  function updateItem(idx: number, patch: Partial<WaybillItem>) {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    );
  }

  function mapSavedWaybillToPdf(w: WaybillApi): PdfWaybill {
    return {
      waybill_number: w.waybill_number || waybillNumber,
      waybill_date: w.waybill_date || waybillDate,
      customer_name: w.customer_name || customerName,
      customer_phone: w.customer_phone || customerPhone,
      delivery_address: w.delivery_address || deliveryAddress,
      quotation_ref: w.quotation_ref || quotationRef,
      driver_name: w.driver_name || driverName,
      vehicle_number: w.vehicle_number || vehicleNumber,
      status: w.status || status,
      notes: w.notes || notes,
      items: (w.items || []).map((it, idx) =>
        // The server already returns `length` on the new shape; legacy rows
        // are normalised by `itemFromLegacy` semantics on the server side too.
        ({
          line_no: it.line_no ?? idx + 1,
          description: it.description,
          unit: it.unit,
          length: Number(it.length ?? 1) || 1,
          quantity: Number(it.quantity || 0),
        }),
      ),
    };
  }

  function buildPdfFromFormState(): PdfWaybill {
    return {
      waybill_number: waybillNumber,
      waybill_date: waybillDate,
      customer_name: customerName,
      customer_phone: customerPhone,
      delivery_address: deliveryAddress,
      quotation_ref: quotationRef,
      driver_name: driverName,
      vehicle_number: vehicleNumber,
      status,
      notes,
      items: items.map((it, idx) => ({
        line_no: idx + 1,
        description: it.description,
        unit: it.unit,
        length: Number(it.length || 1),
        quantity: Number(it.quantity || 0),
      })),
    };
  }

  // Adapter — flatten the legacy PdfWaybill shape into the HTML print
  // component's input so we can keep the same form-level pipeline while
  // upgrading the visual to the old desktop look.
  function mapPdfToPrint(pdf: PdfWaybill): WaybillPrintData {
    return {
      number: pdf.waybill_number,
      customerName: pdf.customer_name,
      customerPhone: pdf.customer_phone ?? "",
      deliveryAddress: pdf.delivery_address ?? "",
      quotationRef: pdf.quotation_ref ?? "",
      waybillDate: pdf.waybill_date ?? undefined,
      driverName: pdf.driver_name ?? "",
      vehicleNumber: pdf.vehicle_number ?? "",
      status: pdf.status,
      notes: pdf.notes ?? "",
      items: pdf.items.map((it) => ({
        description: it.description,
        unit: it.unit,
        length: Number(it.length || 0),
        quantity: Number(it.quantity || 0),
      })),
    };
  }

  function resolvePreparedBy(name?: string | null): string {
    const trimmed = name?.trim();
    if (trimmed) return trimmed;
    return (
      user?.name?.trim() ||
      user?.username?.trim() ||
      user?.email?.trim() ||
      "—"
    );
  }

  async function generatePdf(pdf: PdfWaybill, preparedBy: string) {
    const printCompany = {
      name: (company.name || "BillForge").toString(),
      address: (company.address || "").toString(),
      phone: (company.phone || "").toString(),
      email: (company.email || "").toString(),
    };
    // Profile / Color come from the source quotation (captured on import).
    // Invoice no/date come from the linked sales invoice surfaced by the API
    // (Task 1). Per Task 2 the PDF is unreachable without an invoice, so in
    // practice these are populated whenever the strip renders.
    const printData: WaybillPrintData = {
      ...mapPdfToPrint(pdf),
      profile: quoteProfile || undefined,
      color: quoteColor || undefined,
      invoiceNumber: invoiceNumber || undefined,
      invoiceDate: invoiceDate || undefined,
    };
    return renderReactToPdf(
      <WaybillPrint
        w={printData}
        company={printCompany}
        preparedBy={preparedBy}
      />,
      {
        filename: safePdfFilename("Waybill", pdf.waybill_number || "Draft"),
      },
    );
  }

  function openPdfPreview(
    pdf: { url: string; filename: string },
    waybillNo: string,
  ) {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(pdf.url);
    setPdfFilename(pdf.filename);
    setPdfWaybillNumber(waybillNo);
    setPdfModalOpen(true);
  }

  function handlePdfModalOpenChange(open: boolean) {
    if (!open) {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setPdfFilename("");
      setPdfWaybillNumber("");
      setPdfModalOpen(false);
      if (postSaveNavId != null) {
        nav(`/waybills/${postSaveNavId}`);
        setPostSaveNavId(null);
      }
    } else {
      setPdfModalOpen(true);
    }
  }

  async function handleDownloadPdfFromForm() {
    if (!canPreviewPdf) {
      toast.message(
        "This waybill has no Sales Invoice yet — download is available once an invoice is generated.",
      );
      return;
    }
    try {
      const pdf = await generatePdf(
        buildPdfFromFormState(),
        resolvePreparedBy(createdByName),
      );
      const a = document.createElement("a");
      a.href = pdf.url;
      a.download = pdf.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(pdf.url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    }
  }

  async function handlePreviewPdfFromForm() {
    if (generatingPdf) return;
    if (!canPreviewPdf) {
      toast.message(
        "This waybill has no Sales Invoice yet — preview is available once an invoice is generated.",
      );
      return;
    }
    setGeneratingPdf(true);
    try {
      const pdfData = buildPdfFromFormState();
      const pdf = await generatePdf(pdfData, resolvePreparedBy(createdByName));
      openPdfPreview(pdf, pdfData.waybill_number || "Draft");
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  }

  // Honour the ?openPdf=1 query param from the listing's "Preview PDF" row
  // action — once the waybill (active or cancelled) has finished loading,
  // auto-open the preview exactly once so re-renders don't loop the modal.
  useEffect(() => {
    if (!wantsAutoOpenPdf || autoOpenConsumed) return;
    if (!id) return;
    if (!waybillNumber) return;
    setAutoOpenConsumed(true);
    // Per Task 2, the deep-link must not auto-open the preview when no sales
    // invoice exists for the waybill — show an explanatory toast instead.
    if (!canPreviewPdf) {
      toast.message(
        "This waybill has no Sales Invoice yet — preview is available once an invoice is generated.",
      );
      return;
    }
    void handlePreviewPdfFromForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsAutoOpenPdf, autoOpenConsumed, id, waybillNumber, canPreviewPdf]);

  async function save() {
    if (readOnly) return;
    if (!customerName.trim()) {
      toast.error("Customer name is required");
      return;
    }
    const validItems = items.filter((it) => it.description.trim());
    if (!validItems.length) {
      toast.error("Add at least one line item");
      return;
    }
    // Strict: every line must resolve to a catalog product (no free-text).
    // Editing existing waybills skips this guard so legacy free-text rows
    // can still be re-saved by users who can't add to the catalog.
    if (!editing) {
      const missingProduct = items.findIndex(
        (it) => it.description.trim() && !it.productId,
      );
      if (missingProduct >= 0) {
        setShowProductErrors(true);
        toast.error("Please select a product from the list.");
        requestAnimationFrame(() => {
          const firstBad = document.querySelector<HTMLInputElement>(
            'input[data-strict="true"][data-product-id=""]',
          );
          if (firstBad) {
            firstBad.scrollIntoView({ block: "center", behavior: "smooth" });
            firstBad.focus();
          }
        });
        return;
      }
      if (showProductErrors) setShowProductErrors(false);
    }
    setSaving(true);
    try {
      const body = {
        quote_id: quoteId,
        quotation_ref: quotationRef,
        customer_name: customerName.trim(),
        customer_phone: customerPhone,
        delivery_address: deliveryAddress,
        driver_name: driverName,
        vehicle_number: vehicleNumber,
        notes,
        // Send the flat shape that matches the new `waybill_items.length`
        // column. The server normalises non-positive lengths back to 1.
        // product_id is sent as null for free-text rows; rows that share the
        // same product_id (different lengths) are persisted independently.
        items: validItems.map((it) => ({
          product_id: it.productId,
          description: it.description,
          unit: it.unit,
          length: Number(it.length || 1),
          quantity: Number(it.quantity || 0),
        })),
      };
      let saved: WaybillApi | null = null;
      if (editing) {
        const res = await api.put<{ waybill: WaybillApi }>(
          `/waybills/${id}`,
          body,
        );
        saved = res.waybill ?? null;
        toast.success("Waybill updated");
      } else {
        const res = await api.post<{ waybill: WaybillApi }>("/waybills", body);
        saved = res.waybill ?? null;
        toast.success("Waybill created");
      }
      // A just-saved waybill cannot yet have a sales invoice (creating one
      // is a separate downstream step, and editing is blocked once an invoice
      // exists). Per Task 2 we therefore do NOT auto-open the PDF preview on
      // save — we explain that the preview unlocks once an invoice is raised
      // and route to the saved waybill's detail view.
      const savedHasInvoice =
        !!saved?.has_sales_invoice || !!saved?.invoice_number;
      if (saved?.created_by_name !== undefined) {
        setCreatedByName(saved.created_by_name ?? null);
      }
      if (savedHasInvoice) {
        try {
          const pdfSource: PdfWaybill = saved
            ? mapSavedWaybillToPdf(saved)
            : buildPdfFromFormState();
          const savedName = saved?.created_by_name ?? createdByName;
          const pdf = await generatePdf(
            pdfSource,
            resolvePreparedBy(savedName),
          );
          const assignedNumber =
            saved?.waybill_number || waybillNumber || "Draft";
          if (saved?.waybill_number) setWaybillNumber(saved.waybill_number);
          if (saved?.id != null) setPostSaveNavId(saved.id);
          openPdfPreview(pdf, assignedNumber);
        } catch (pdfErr) {
          // PDF is a courtesy step — never block save success on it.
          toast.error((pdfErr as Error).message || "Could not render PDF");
          nav("/waybills");
        }
      } else {
        toast.message(
          "This waybill has no Sales Invoice yet — preview is available once an invoice is generated.",
        );
        if (saved?.id != null) nav(`/waybills/${saved.id}`);
        else nav("/waybills");
      }
    } catch (err) {
      toast.error((err as Error).message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const title = isCancelledPreview
    ? "View Cancelled Waybill"
    : lockedByInvoice || isPreview
      ? "View Waybill"
      : editing
        ? "Edit Waybill"
        : "New Waybill";

  return (
    <div className="space-y-4 max-w-[1200px]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav("/waybills")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        </div>
        <div className="flex items-center gap-2">
          {Boolean(id) ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePreviewPdfFromForm}
                    disabled={generatingPdf || !canPreviewPdf}
                    title={canPreviewPdf ? "Preview PDF" : pdfGateReason}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {generatingPdf ? "Preparing..." : "Preview PDF"}
                  </Button>
                </span>
              </TooltipTrigger>
              {!canPreviewPdf ? (
                <TooltipContent>{pdfGateReason}</TooltipContent>
              ) : null}
            </Tooltip>
          ) : null}
          {(isPreview || editing) && !isCancelledPreview ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    variant="outline"
                    onClick={handleDownloadPdfFromForm}
                    disabled={!canPreviewPdf}
                    title={
                      canPreviewPdf
                        ? "Download PDF of current waybill"
                        : pdfGateReason
                    }
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </span>
              </TooltipTrigger>
              {!canPreviewPdf ? (
                <TooltipContent>{pdfGateReason}</TooltipContent>
              ) : null}
            </Tooltip>
          ) : null}
          {!readOnly && canEdit && !isCancelledPreview ? (
            <Button
              className="bg-violet-500 hover:bg-violet-400 text-white"
              onClick={save}
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          ) : null}
        </div>
      </div>

      {isCancelledPreview && cancelMeta ? (
        <p className="text-sm text-rose-700 dark:text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded-lg px-3 py-2">
          Cancelled — {cancelMeta.reason}
          {cancelMeta.cancelledBy
            ? ` (by ${cancelMeta.cancelledBy}${cancelMeta.cancelledAt ? `, ${cancelMeta.cancelledAt}` : ""})`
            : ""}
        </p>
      ) : null}

      {isPreview && !isCancelledPreview && !lockedByInvoice ? (
        <p className="text-sm text-muted-foreground border rounded-lg px-3 py-2">
          View only — open Edit from the list to make changes.
        </p>
      ) : null}

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="text-base">Waybill Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          {waybillNumber ? (
            <div className="space-y-1.5">
              <Label>Waybill #</Label>
              <Input
                value={waybillNumber}
                readOnly
                className="h-10 font-mono"
              />
            </div>
          ) : null}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Customer Name *</Label>
              <EntityCombobox
                aria-label="Customer name"
                className="h-10"
                placeholder="Select customer (fully paid quotations only)"
                value={customerName}
                disabled={readOnly}
                options={customerOptions.map((name) => ({
                  id: name,
                  name,
                }))}
                emptyText="No fully paid quotations available for waybill delivery."
                onSelect={(opt) => {
                  setCustomerName(opt.name);
                  setQuoteId(null);
                  setQuotationRef("");
                  setDeliveryAddress("");
                  setQuoteProfile("");
                  setQuoteColor("");
                }}
                onClear={() => {
                  setCustomerName("");
                  setQuoteId(null);
                  setQuotationRef("");
                  setDeliveryAddress("");
                  setQuoteProfile("");
                  setQuoteColor("");
                }}
              />
              {customerOptions.length === 0 && !readOnly ? (
                <p className="text-xs text-muted-foreground">
                  No fully paid quotations available for waybill delivery.
                </p>
              ) : null}
              {customerName && selectedCustomerId != null ? (
                <p className="text-xs text-muted-foreground">
                  Available credit:{" "}
                  <span className="font-medium text-foreground">
                    GHS {formatNumber(selectedCustomerCredit)}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label>Quotation Number</Label>
              <EntityCombobox
                aria-label="Quotation number"
                className="h-10"
                value={quotationRef}
                disabled={readOnly || !customerName}
                placeholder={
                  customerName
                    ? "Select fully paid quotation"
                    : "Select customer first"
                }
                options={customerQuotes.map((q) => ({
                  id: q.id,
                  name: q.quote_number,
                  secondary: q.location || q.telephone || null,
                }))}
                emptyText="No fully paid quotations for this customer."
                onSelect={(opt) => importQuotation(String(opt.id))}
                onClear={() => {
                  setQuoteId(null);
                  setQuotationRef("");
                }}
              />
              {!readOnly && !editing ? (
                <p className="text-[11px] leading-tight rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2 py-1.5">
                  Only finalised quotations are listed. Draft quotations must
                  be finalised before a waybill can be raised.
                </p>
              ) : null}
              {!readOnly && !editing && quoteId != null && willSettleFromCredit ? (
                <p className="text-[11px] leading-tight rounded-md border border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300 px-2 py-1.5">
                  This quote's outstanding balance of GHS{" "}
                  {formatNumber(selectedQuoteOutstanding)} will be settled from
                  the customer's available credit (GHS{" "}
                  {formatNumber(selectedCustomerCredit)}) when the waybill is
                  created.
                </p>
              ) : null}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Quotation Ref</Label>
              <Input
                value={quotationRef}
                readOnly
                className="h-10 font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                readOnly={derivedFieldsLocked}
                disabled={derivedFieldsLocked}
                aria-readonly={derivedFieldsLocked ? "true" : undefined}
                tabIndex={derivedFieldsLocked ? -1 : undefined}
                className={
                  derivedFieldsLocked
                    ? "h-10 bg-muted/30 text-foreground cursor-not-allowed"
                    : "h-10"
                }
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Delivery Address</Label>
            <Input
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              readOnly={derivedFieldsLocked}
              disabled={derivedFieldsLocked}
              aria-readonly={derivedFieldsLocked ? "true" : undefined}
              tabIndex={derivedFieldsLocked ? -1 : undefined}
              placeholder={
                quoteId ? "No location on quotation" : "Select a quotation to load location"
              }
              className={
                derivedFieldsLocked
                  ? "h-10 bg-muted/30 text-foreground cursor-not-allowed"
                  : "h-10"
              }
            />
            <p className="text-xs text-muted-foreground">
              Filled automatically from the quotation location.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Waybill Date</Label>
              <Input
                type="date"
                value={waybillDate}
                readOnly
                disabled
                aria-readonly="true"
                tabIndex={-1}
                className="h-10 bg-muted/30 text-foreground cursor-not-allowed"
              />
              {!isExisting && !isCancelledPreview ? (
                <p className="text-xs text-muted-foreground">
                  Stamped automatically on save.
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label>Driver</Label>
              <Input
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                readOnly={readOnly}
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Vehicle No.</Label>
              <Input
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                readOnly={readOnly}
                className="h-10"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              readOnly={derivedFieldsLocked}
              disabled={derivedFieldsLocked}
              aria-readonly={derivedFieldsLocked ? "true" : undefined}
              tabIndex={derivedFieldsLocked ? -1 : undefined}
              className={
                derivedFieldsLocked
                  ? "bg-muted/30 text-foreground cursor-not-allowed"
                  : undefined
              }
            />
          </div>
        </CardContent>
      </Card>

      {showImportLockBanner ? (
        <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-lg px-3 py-2">
          <Lock className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Items are locked because they were imported from{" "}
            <span className="font-mono">{quotationRef || "the quotation"}</span>
            . To change items, issue a new quotation.
          </span>
        </p>
      ) : null}

      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Items</CardTitle>
          {!itemsLocked ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setItems((p) => [...p, { ...BLANK_ITEM }])}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Line
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="pt-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="w-12 text-center">NO.</TableHead>
                <TableHead className="min-w-[260px]">
                  PRODUCT DESCRIPTION
                </TableHead>
                <TableHead className="w-28">UNIT</TableHead>
                <TableHead className="w-28 text-right">LENGTH</TableHead>
                <TableHead className="w-28 text-right">QTY</TableHead>
                <TableHead className="w-32 text-right">
                  TOTAL QUANTITY
                </TableHead>
                {!itemsLocked ? <TableHead className="w-12" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it, i) => (
                <TableRow key={i}>
                  <TableCell className="text-center font-mono text-sm text-muted-foreground">
                    {i + 1}
                  </TableCell>
                  <TableCell>
                    {/*
                      Same typeahead used on the Quotation form. Free-text is
                      preserved via onTextChange; picking a catalog item
                      auto-fills unit while leaving length/qty under the user's
                      control (length defaults to 1 from BLANK_ITEM).
                    */}
                    {/*
                      The Waybill PRODUCT DESCRIPTION field reuses the EXACT
                      same `ProductCombobox` used by the Quotation form — same
                      debounced /products?q= autocomplete, same portal-based
                      dropdown, same keyboard nav. Selecting a product fills
                      description+unit and stamps the productId on the row;
                      typing free-text clears productId so the row persists as
                      a non-catalog entry. Each row owns its own productId,
                      so the same product can appear on multiple rows with
                      different LENGTH values without merging.
                    */}
                    <div className="space-y-1">
                      <ProductCombobox
                        value={it.description}
                        productId={it.productId}
                        // Reuse the catalog already fetched via the
                        // waybills-scoped form-support endpoint so the
                        // combobox does not hit /api/products directly.
                        options={productCatalog}
                        onTextChange={(text) =>
                          updateItem(i, {
                            description: text,
                            productId:
                              text === it.description ? it.productId : null,
                          })
                        }
                        onSelect={(p) =>
                          updateItem(i, {
                            productId: p.id,
                            description: p.name,
                            unit: p.unit || it.unit || "Pieces",
                          })
                        }
                        onClear={() =>
                          updateItem(i, {
                            productId: null,
                            description: "",
                          })
                        }
                        disabled={itemsLocked}
                        // Strict: a waybill row must resolve to a catalog
                        // product (same rule as the Quotation form). The user
                        // can type to search; Save is what blocks unresolved
                        // rows.
                        strict={!itemsLocked}
                        invalid={
                          !itemsLocked &&
                          showProductErrors &&
                          it.productId == null
                        }
                        placeholder="Search catalog (free-text not accepted on save)…"
                        aria-label={`Item ${i + 1} description`}
                        className={
                          itemsLocked
                            ? "h-9 bg-muted/30 text-foreground"
                            : "h-9"
                        }
                      />
                      {!itemsLocked &&
                      showProductErrors &&
                      it.productId == null ? (
                        <p className="text-[11px] text-destructive leading-tight">
                          Please select a product from the list.
                        </p>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={it.unit}
                      onChange={(e) => updateItem(i, { unit: e.target.value })}
                      readOnly={itemsLocked}
                      disabled={itemsLocked}
                      aria-readonly={itemsLocked ? "true" : undefined}
                      tabIndex={itemsLocked ? -1 : undefined}
                      className={
                        itemsLocked
                          ? "h-9 bg-muted/30 text-foreground"
                          : "h-9"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={displayNumber(`wb-length-${i}`, it.length)}
                      onFocus={() => setActiveNumberField(`wb-length-${i}`)}
                      onBlur={() => setActiveNumberField(null)}
                      onChange={(e) =>
                        updateItem(i, { length: Number(e.target.value || 0) })
                      }
                      readOnly={itemsLocked}
                      disabled={itemsLocked}
                      aria-readonly={itemsLocked ? "true" : undefined}
                      tabIndex={itemsLocked ? -1 : undefined}
                      className={
                        itemsLocked
                          ? "h-9 text-right bg-muted/30 text-foreground"
                          : "h-9 text-right"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={displayNumber(`wb-qty-${i}`, it.quantity)}
                      onFocus={() => setActiveNumberField(`wb-qty-${i}`)}
                      onBlur={() => setActiveNumberField(null)}
                      onChange={(e) =>
                        updateItem(i, {
                          quantity: Number(e.target.value || 0),
                        })
                      }
                      readOnly={itemsLocked}
                      disabled={itemsLocked}
                      aria-readonly={itemsLocked ? "true" : undefined}
                      tabIndex={itemsLocked ? -1 : undefined}
                      className={
                        itemsLocked
                          ? "h-9 text-right bg-muted/30 text-foreground"
                          : "h-9 text-right"
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {it.quantity > 0 || it.length > 0
                      ? formatNumber(rowTotal(it))
                      : "0.00"}
                  </TableCell>
                  {!itemsLocked ? (
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          setItems((prev) =>
                            prev.length > 1
                              ? prev.filter((_, idx) => idx !== i)
                              : prev,
                          )
                        }
                        title="Remove row"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className="bg-muted/60">
                <TableCell colSpan={5} className="text-right font-semibold">
                  TOTAL
                </TableCell>
                <TableCell className="text-right font-mono font-semibold">
                  {formatNumber(totalQuantitySum)}
                </TableCell>
                {!itemsLocked ? <TableCell /> : null}
              </TableRow>
            </TableFooter>
          </Table>
          <p className="text-xs text-muted-foreground mt-3 px-1">
            LENGTH defaults to 1 for items measured in pieces. TOTAL QUANTITY
            is computed live as LENGTH × QTY.
          </p>
        </CardContent>
      </Card>

      <PdfPreviewModal
        open={pdfModalOpen}
        onOpenChange={handlePdfModalOpenChange}
        pdfUrl={pdfPreviewUrl}
        filename={pdfFilename}
        title="Waybill PDF"
        documentNumber={pdfWaybillNumber}
      />
    </div>
  );
}
